import { useState, useEffect } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { api } from '../../../utils/api';
import BrandModel from './BrandModel.jsx';
import './quote-assessment.css';

const MAX = 4;
// The fenced JSON block the worker's parser reads (reverse-image.js JSON_TAIL);
// the operator sees its fields as Brand / Model / confidence, not as code.
const JSON_TAIL = /```json\s*[\s\S]*?```/i;

// The prose of an answer without its machine-readable tail. Exported for unit testing.
export function stripJsonTail(answer) {
  return typeof answer === 'string' ? answer.replace(JSON_TAIL, '').trim() : '';
}

// The customer's own words (their trade-in submission), assembled into the seed
// text the lookup weighs: the free-text note plus each submitted item line.
// Exported for unit testing.
export function assembleCustomerText(submission) {
  if (!submission) return '';
  const lines = [];
  if (submission.notes && submission.notes.trim()) lines.push(submission.notes.trim());
  for (const it of submission.items || []) {
    const name = [it.make, it.model].filter(s => s && s.trim()).join(' ').trim();
    const facts = [it.category, it.condition].filter(Boolean).join(', ');
    const desc = it.description && it.description.trim();
    const line = [name, facts && `(${facts})`, desc].filter(Boolean).join(' ').trim();
    if (line) lines.push(line);
  }
  return lines.join('\n');
}

// Split the deal's photos into the ones that belong to THIS item and the rest,
// this item's first. The item's photos come from the KG identify card
// (ItemIdentityCard.photos), matched by R2 key against the flat public URLs the
// popup lists — a photo may carry a signed_url, so we match on the key, never on
// the full URL. Exported for unit testing.
export function partitionItemPhotos(images, itemPhotos) {
  const keys = (itemPhotos || []).map(p => p?.r2_key).filter(Boolean);
  const isItem = url => keys.some(k => url === k || url.endsWith(`/${k}`));
  const mine = (images || []).filter(isItem);
  const others = (images || []).filter(url => !isItem(url));
  return { mine, others };
}

// The answer line: engine badge, then Brand / Model as the identity card shows
// them, with the model's confidence. An answer the worker could not split
// (no JSON tail) falls back to the one-line best guess.
function Headline({ result }) {
  const parsed = result.parsed;
  const split = parsed && (parsed.brand || parsed.model);
  return (
    <div className="qa-revlookup-guess">
      <span className={`qa-badge qa-badge-${result.engine}`}>{result.engine.toUpperCase()}</span>
      {split ? (
        <>
          <BrandModel brand={parsed.brand} model={parsed.model} />
          {typeof parsed.confidence === 'number' && (
            <span className="qa-idc-conf" title="The model's own confidence in this answer">{Math.round(parsed.confidence * 100)}%</span>
          )}
        </>
      ) : result.bestGuess
        ? <strong>{result.bestGuess}</strong>
        : <span className="qa-muted">No best guess</span>}
    </div>
  );
}

// lookupPath overrides the deals-surface endpoint so pim-gated callers
// (Catalog) can use the /api/graph mirror instead of the deals-gated route.
export default function ReverseLookupPopup({ item, images, itemPhotos = [], dealId, lookupPath, onClose }) {
  // This item's own photos preselected, up to the lookup's photo cap.
  const [selected, setSelected] = useState(() => new Set(partitionItemPhotos(images, itemPhotos).mine.slice(0, MAX)));
  const [customerText, setCustomerText] = useState('');
  const [subError, setSubError] = useState(false);
  const [state, setState] = useState({ idle: true });

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Deals surface only: prefill the customer's own description. Catalog products
  // (lookupPath set) carry no trade-in submission, so skip the fetch there.
  useEffect(() => {
    if (!dealId || lookupPath || images.length === 0) return;
    let alive = true;
    api.get(`/api/deals/${dealId}/submission`)
      // Only prefill if the operator hasn't started typing — a slow fetch must
      // not clobber their edits when it lands.
      .then(r => { if (alive) setCustomerText(prev => prev || assembleCustomerText(r.submission)); })
      .catch(() => { if (alive) setSubError(true); });
    return () => { alive = false; };
  }, [dealId, lookupPath, images.length]);

  const toggle = url => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(url)) next.delete(url);
    else if (next.size < MAX) next.add(url);
    return next;
  });

  const runLookup = () => {
    const imageUrls = [...selected];
    if (!imageUrls.length) return;
    setState({ loading: true });
    api.post(lookupPath || `/api/deals/${dealId}/reverse-image-lookup`, { imageUrls, customerText })
      .then(r => {
        if (r.status === 'error') setState({ error: r.error || 'Lookup failed', debug: r.debug });
        else setState({ result: r });
      })
      .catch(e => setState({ error: e.message || 'Lookup failed' }));
  };

  // This item's photos first, the rest after — one strip, item photos preselected.
  const { mine, others } = partitionItemPhotos(images, itemPhotos);
  const ordered = [...mine, ...others];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content qa-revlookup" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Identify item — {item?.name || 'item'}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body-scroll">
          {images.length === 0 && <div className="qa-muted">No photos on this deal.</div>}
          {images.length > 0 && (
            <>
              <div className="qa-revlookup-pick">
                <div className="qa-muted">Pick up to {MAX} photos (headstock + serial work best):</div>
                <div className="qa-revlookup-strip">
                  {ordered.map((url, i) => (
                    <button key={url} type="button"
                      className={`qa-revlookup-thumb ${selected.has(url) ? 'on' : ''}`}
                      onClick={() => toggle(url)}>
                      <img src={url} alt={`photo ${i + 1}`} />
                    </button>
                  ))}
                </div>
                <label className="qa-revlookup-said">
                  <span className="qa-muted">What the customer said (edit to refine):</span>
                  <textarea aria-label="What the customer said" rows={3}
                    value={customerText} onChange={e => setCustomerText(e.target.value)}
                    placeholder="The customer's description, if any" />
                </label>
                {subError && (
                  <div className="qa-muted">Couldn't load the customer's submission — type it in if you have it.</div>
                )}
                <button className="qa-revlookup-go" disabled={selected.size === 0 || state.loading}
                  onClick={runLookup}>
                  {state.loading ? 'Identifying…' : `Look up (${selected.size})`}
                </button>
              </div>

              {state.error && <div className="qa-error" role="alert">{state.error}</div>}
              {state.result && (
                <div className="qa-revlookup-results">
                  <Headline result={state.result} />
                  {stripJsonTail(state.result.answer) && (
                    <div className="qa-revlookup-answer"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(stripJsonTail(state.result.answer))) }} />
                  )}
                  {state.result.matches.length > 0 && (
                    <div className="qa-revlookup-grid">
                      {state.result.matches.map((m, i) => (
                        <a key={i} className="qa-revlookup-match" href={m.sourceUrl} target="_blank" rel="noreferrer">
                          {m.thumbnail && <img src={m.thumbnail} alt="" />}
                          <span>{m.title || m.sourceUrl}</span>
                        </a>
                      ))}
                    </div>
                  )}
                  {state.result.matches.length === 0 && <div className="qa-muted">No web sources cited.</div>}
                  {state.result.debug && (
                    <details className="qa-revlookup-debug">
                      <summary className="qa-muted">Diagnostics</summary>
                      {state.result.debug.map((d, i) => <div key={i} className="qa-muted">{d}</div>)}
                    </details>
                  )}
                </div>
              )}
              {state.debug && (
                <details className="qa-revlookup-debug" open>
                  <summary className="qa-muted">Diagnostics</summary>
                  {state.debug.map((d, i) => <div key={i} className="qa-muted">{d}</div>)}
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
